"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EmailFieldLogic = void 0;
const email_validation_1 = require("@smartsuite/email-validation");
const clipboard_1 = require("../../clipboard");
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const text_1 = require("../../utils/text");
const intl_1 = require("../../locale/intl");
const email_fieldtype_1 = require("../../safe-field-value/text-fields/email-field/email.fieldtype");
const texts_1 = require("../../texts");
const filter_1 = require("../../utils/filter");
const validation_1 = require("../../validation");
const validation_2 = require("../../validation/validation");
const bulk_update_1 = require("../../utils/bulk-update");
class EmailFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "emailfield";
        this.label = (0, intl_1.$t)(texts_1.emailFieldText.email);
        this.icon = "email";
        this.iconColor = "#883CD0";
        this.placeholder = (0, intl_1.$t)(texts_1.emailFieldText.enter_an_email_address);
        this.searchSynonyms = ["@"];
        this.removeInvalid = true;
        this.fieldValueGetter = new email_fieldtype_1.EmailFieldValueGetter();
        this.lookupMode = "lookup";
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isListExact,
            is_not: comparisons_1.isNotListExact,
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: comparisons_1.containsList,
            not_contains: comparisons_1.notContainsList,
        };
        this.clipboardPasteRules = {
            emailfield: clipboard_1.clipboardEmailSelf,
            recordtitlefield: clipboard_1.clipboardEmailSelf,
            textfield: clipboard_1.clipboardTextToEmail,
            textareafield: clipboard_1.clipboardTextToEmail,
            richtextareafield: clipboard_1.clipboardTextToEmail,
            tagsfield: clipboard_1.clipboardTextToEmail,
            singleselectfield: clipboard_1.clipboardTextToEmail,
            multipleselectfield: clipboard_1.clipboardTextToEmail,
            linkedrecordfield: clipboard_1.clipboardJsonToEmail,
            subitemsfield: clipboard_1.clipboardJsonToEmail,
            unknownfield: clipboard_1.clipboardTextToEmail,
        };
    }
    get validators() {
        const uniqueValidator = (0, validation_1.uniqueRecordFieldValueWithContext)({
            checkUniqueness: this.appRecordsService.checkUniqueness.bind(this.appRecordsService),
        });
        return { required: validation_2.required, emailField: validation_1.emailField, unique: uniqueValidator };
    }
    getEmptyValue() {
        return [];
    }
    getInitialFieldValue(startValue) {
        return startValue ? [startValue] : this.getEmptyValue();
    }
    getGroupValues(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return [[]];
        }
        return fieldValue
            .filter(filter_1.filterUniqueValues)
            .map((valueItem) => [valueItem]);
    }
    prepareValueForClipboard(fieldValue, _field) {
        return { text: fieldValue.join("; "), rawValue: fieldValue };
    }
    getInlineEditRules(field) {
        const { params } = field;
        const isSingle = params.entries_allowed === "single";
        return {
            ...super.getInlineEditRules(field),
            deleteValueOnFirstKeypress: true,
            editorWidth: isSingle ? "100%" : "400px",
        };
    }
    removeInvalidValues(values) {
        const filteredValues = values.filter((email) => (0, email_validation_1.validateEmailAddress)(email));
        // return array without invalid values
        if (values.length !== filteredValues.length) {
            return filteredValues;
        }
        // if filtered values are the same to initial,
        // that means that unique rule is validated
        return [];
    }
    processFilterValue(filterValue, comparison) {
        /**
         * We allow specifying multiple filter values separated by ;, and space.
         * They are applied as OR.
         */
        const filterComparisons = ["is", "is_not", "contains", "not_contains"];
        if (filterComparisons.includes(comparison)) {
            return (0, text_1.processMultipleFilterValue)(filterValue, types_1.emailFieldDelimetersRegex);
        }
        return filterValue;
    }
    filterToRecordValue(filterValue, comparison, field) {
        if (comparison !== "is" || !filterValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const value = (0, text_1.processMultipleFilterValue)(filterValue, types_1.emailFieldDelimetersRegex);
        const hasInvalidValue = value.some((email) => !(0, email_validation_1.validateEmailAddress)(email));
        if (hasInvalidValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        return value;
    }
    processSearch(searchTerm, fieldValue, field, userPreferences, record) {
        const preparedFieldValue = this.searchKey(fieldValue, field, userPreferences, record);
        return (0, text_1.isMultipleTermInSearch)(searchTerm, preparedFieldValue, "every");
    }
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, field) {
        if (this.isValueEmpty(previousValue, field) ||
            this.isValueEmpty(currentValue, field)) {
            return { previousValue, currentValue };
        }
        return {
            previousValue: getDifference(previousValue, currentValue),
            currentValue: getDifference(currentValue, previousValue),
        };
    }
    replaceValue(field, record, sourceValue = [], targetValue = [], _userPreferences) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        if (field.params.entries_allowed === "single") {
            return super.applyBulkUpdate(field, value, update);
        }
        return (0, bulk_update_1.applyBulkUpdateToPrimitiveArray)(value, update);
    }
}
exports.EmailFieldLogic = EmailFieldLogic;
/**
 * Get items that are in array1 but not in array2.
 * If there are any duplicates, it is necessary to take this into account.
 *
 * @param array1
 * @param array2
 */
const getDifference = (array1, array2) => {
    const diff = [...array1];
    const compare = [...array2];
    array1.forEach((email) => {
        if (compare.includes(email)) {
            diff.splice(diff.indexOf(email), 1);
            compare.splice(compare.indexOf(email), 1);
        }
    });
    return diff;
};
